import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Card, Col, DatePicker, Row, Select, Space, Tabs, Tag, Tooltip } from "antd";
import { SyncOutlined, UserOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import PageHeader from "../view/pageheader";
import TableComponent from "../fields/TableComponent";
import { fetchAuditLogs, fetchAuditPresence } from "../store/reducer/auditSlice";

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Option } = Select;

const ACTION_LABELS = {
    login: { label: "Login", color: "green" },
    login_failed: { label: "Login Failed", color: "red" },
    logout: { label: "Logout", color: "default" },
    room_created: { label: "Room Created", color: "blue" },
    item_saved: { label: "Item Saved", color: "cyan" },
    item_uploaded: { label: "Item Uploaded", color: "geekblue" },
    item_claimed: { label: "Item Claimed", color: "purple" },
    item_viewed: { label: "Item Viewed", color: "default" },
    doc_uploaded: { label: "Doc Uploaded", color: "orange" },
    claim_settled: { label: "Claim Settled", color: "volcano" },
    ai_estimation: { label: "AI Estimation", color: "gold" },
    ai_naming: { label: "AI Naming", color: "lime" },
};

const USER_TYPE_TABS = [
    { key: "", label: "All" },
    { key: "admin", label: "Admin / Staff" },
    { key: "portal", label: "Portal Users" },
    { key: "mobile", label: "Mobile Clients" },
    { key: "staff", label: "Job Staff" },
    { key: "photo", label: "Photographer" },
];

const PRESENCE_ROLE_LABELS = {
    admin: { label: "Admin", color: "#1677ff" },
    portal: { label: "Portal Users", color: "#722ed1" },
    mobile: { label: "Mobile Clients", color: "#13c2c2" },
    staff: { label: "Job Staff", color: "#52c41a" },
    photo: { label: "Photographer", color: "#fa8c16" },
};

const PRESENCE_POLL_MS = 30000;

const AuditTrail = () => {
    const dispatch = useDispatch();
    const { logsData, logsLoading, presenceData, presenceLoading } = useSelector((s) => s.audit);

    const [activeTab, setActiveTab] = useState("");
    const [filters, setFilters] = useState({ action: "", date_from: "", date_to: "", page: 1, limit: 20 });
    const presenceTimer = useRef(null);

    function loadLogs(extra = {}) {
        dispatch(fetchAuditLogs({ user_type: activeTab, ...filters, ...extra }));
    }

    function loadPresence() {
        dispatch(fetchAuditPresence());
    }

    useEffect(() => {
        loadLogs();
    }, [activeTab]);

    useEffect(() => {
        loadPresence();
        presenceTimer.current = setInterval(loadPresence, PRESENCE_POLL_MS);
        return () => clearInterval(presenceTimer.current);
    }, []);

    function handleTableChange(payload) {
        const next = { ...filters, page: payload.page, limit: payload.limit };
        setFilters(next);
        loadLogs({ ...next, user_type: activeTab });
    }

    function handleDateChange(_, [from, to]) {
        const next = { ...filters, date_from: from || "", date_to: to || "", page: 1 };
        setFilters(next);
        loadLogs({ ...next, user_type: activeTab });
    }

    function handleActionFilter(val) {
        const next = { ...filters, action: val || "", page: 1 };
        setFilters(next);
        loadLogs({ ...next, user_type: activeTab });
    }

    function handleRefresh() {
        loadLogs();
        loadPresence();
    }

    const columns = [
        {
            title: "Time",
            key: "created_at",
            width: 160,
            render: (r) => (
                <Tooltip title={dayjs(r.created_at).format("MM/DD/YYYY HH:mm:ss")}>
                    <span style={{ fontSize: 12 }}>{dayjs(r.created_at).fromNow()}</span>
                </Tooltip>
            ),
            sorter: true,
        },
        {
            title: "User",
            key: "identifier",
            render: (r) => (
                <Space direction="vertical" size={0}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{r.identifier || `ID: ${r.user_id}`}</span>
                    <Tag style={{ fontSize: 10 }} color={PRESENCE_ROLE_LABELS[r.user_type]?.color}>{r.user_type?.toUpperCase()}</Tag>
                </Space>
            ),
        },
        {
            title: "Action",
            key: "action",
            render: (r) => {
                const a = ACTION_LABELS[r.action] || { label: r.action, color: "default" };
                return <Tag color={a.color}>{a.label}</Tag>;
            },
        },
        {
            title: "Entity",
            key: "entity_name",
            render: (r) =>
                r.entity_name ? (
                    <Space direction="vertical" size={0}>
                        <span style={{ fontSize: 12 }}>{r.entity_name}</span>
                        {r.entity_type && <span style={{ fontSize: 11, color: "#8c8c8c" }}>{r.entity_type}</span>}
                    </Space>
                ) : "—",
        },
        {
            title: "Job",
            key: "job_name",
            render: (r) => r.job_name || (r.job_id ? `#${r.job_id}` : "—"),
        },
        {
            title: "Status",
            key: "status",
            render: (r) => (
                <Tag color={r.status === "failed" ? "red" : "green"}>
                    {r.status === "failed" ? "Failed" : "Success"}
                </Tag>
            ),
        },
        {
            title: "IP",
            key: "ip_address",
            render: (r) => <span style={{ fontSize: 11, color: "#8c8c8c" }}>{r.ip_address || "—"}</span>,
        },
    ];

    const totalOnline = Object.values(presenceData).reduce((acc, arr) => acc + (arr?.length || 0), 0);

    const tabItems = USER_TYPE_TABS.map((t) => ({ key: t.key, label: t.label }));

    return (
        <div className="container-fluid">
            <PageHeader HeaderText="Audit Trail" Breadcrumb={[{ name: "Audit Trail" }]} />

            {/* Online Presence Panel */}
            <Card
                style={{ marginBottom: 16 }}
                size="small"
                title={
                    <Space>
                        <ClockCircleOutlined style={{ color: "#52c41a" }} />
                        <span>Online Now</span>
                        <Badge count={totalOnline} style={{ backgroundColor: "#52c41a" }} />
                        <span style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 400 }}>
                            (active in last 10 min · refreshes every 30s)
                        </span>
                    </Space>
                }
                extra={
                    <Button size="small" icon={<SyncOutlined spin={presenceLoading} />} onClick={loadPresence}>
                        Refresh
                    </Button>
                }
            >
                <Row gutter={[12, 8]}>
                    {Object.entries(PRESENCE_ROLE_LABELS).map(([role, meta]) => {
                        const members = presenceData[role] || [];
                        return (
                            <Col key={role} xs={24} sm={12} md={8} lg={6} xl={4}>
                                <Card
                                    size="small"
                                    style={{ borderLeft: `3px solid ${meta.color}`, borderRadius: 6 }}
                                    styles={{ body: { padding: "8px 12px" } }}
                                >
                                    <div style={{ fontWeight: 600, color: meta.color, marginBottom: 4, fontSize: 12 }}>
                                        {meta.label}
                                        <Badge count={members.length} style={{ marginLeft: 8, backgroundColor: meta.color }} showZero />
                                    </div>
                                    {members.length === 0 ? (
                                        <span style={{ fontSize: 11, color: "#bfbfbf" }}>No one online</span>
                                    ) : (
                                        <div style={{ maxHeight: 72, overflowY: "auto" }}>
                                            {members.map((m) => (
                                                <div key={m.id} style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                                                    <UserOutlined style={{ fontSize: 10, color: "#8c8c8c" }} />
                                                    <span>{m.name}</span>
                                                    {m.jobId && <span style={{ color: "#8c8c8c" }}>· job #{m.jobId}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </Card>

            {/* Audit Log Table */}
            <Card
                size="small"
                title="Activity Log"
                extra={
                    <Space wrap>
                        <Select
                            allowClear
                            placeholder="Filter by action"
                            style={{ width: 180 }}
                            value={filters.action || undefined}
                            onChange={handleActionFilter}
                            size="small"
                        >
                            {Object.entries(ACTION_LABELS).map(([key, val]) => (
                                <Option key={key} value={key}>
                                    <Tag color={val.color} style={{ margin: 0 }}>{val.label}</Tag>
                                </Option>
                            ))}
                        </Select>
                        <RangePicker
                            size="small"
                            format="YYYY-MM-DD"
                            onChange={handleDateChange}
                        />
                        <Button size="small" icon={<SyncOutlined spin={logsLoading} />} onClick={handleRefresh}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => { setActiveTab(key); setFilters({ ...filters, page: 1 }); }}
                    items={tabItems}
                    size="small"
                    style={{ marginBottom: 12 }}
                />
                <TableComponent
                    columns={columns}
                    dataSource={logsData?.rows || []}
                    TotalDataCount={logsData?.count || 0}
                    loading={logsLoading}
                    onChangeDispatch={handleTableChange}
                />
            </Card>
        </div>
    );
};

export default AuditTrail;
